import {createListenerMiddleware, isAnyOf} from '@reduxjs/toolkit';
import {deleteDoc, setDoc} from 'firebase/firestore';
import {auth} from '../../firebase/firebase';
import {playlistDoc} from '../../firebase/firestore';
import {
    addTrack,
    createPlaylist,
    deletePlaylist,
    removeTrack,
    renamePlaylist,
    reorderTracks,
    setPlaylistVisibility,
} from '../slices/userPlaylists/reducers';
import type {RootState} from '../store';

export const playlistsSyncMiddleware = createListenerMiddleware();

// Extracts the playlist id from any of the mutation actions we care about.
const getPlaylistIdFromAction = (payload: unknown): string | undefined => {
    if (typeof payload === 'string') return payload;
    if (typeof payload !== 'object' || payload === null) return undefined;
    if ('id' in payload) return (payload as {id: string}).id;
    if ('playlistId' in payload) return (payload as {playlistId: string}).playlistId;
    return undefined;
};

// Mirror local mutations to Firestore for the signed-in user.
// `setAllPlaylists` is deliberately excluded — it's how we apply remote state, so echoing it would loop.
playlistsSyncMiddleware.startListening({
    matcher: isAnyOf(
        createPlaylist,
        renamePlaylist,
        addTrack,
        removeTrack,
        reorderTracks,
        setPlaylistVisibility,
    ),
    effect: async (action, api) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        const playlistId = getPlaylistIdFromAction(action.payload);
        if (!playlistId) return;

        const playlist = (api.getState() as RootState).userPlaylists.playlists[playlistId];
        if (!playlist || playlist.ownerId !== uid) return;

        try {
            await setDoc(playlistDoc(playlistId), playlist);
        } catch (err) {
            console.error('Firestore sync (write) failed', err);
        }
    },
});

playlistsSyncMiddleware.startListening({
    actionCreator: deletePlaylist,
    effect: async (action) => {
        if (!auth.currentUser) return;
        try {
            await deleteDoc(playlistDoc(action.payload));
        } catch (err) {
            console.error('Firestore sync (delete) failed', err);
        }
    },
});
